import { parseUTC, fmtDate, utcDatetimeToLocalInput, localInputToUtcDatetime } from '../utils/dateUtils';
import React, { useEffect, useState, useCallback } from 'react';
import { reminderApi } from '../api';
import { Btn, PageHeader, EmptyState, Input } from '../components/ui';

const REPEAT_OPTS = [
  { value: 'NONE',   label: 'No repeat' },
  { value: 'DAILY',  label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
];

function ReminderCard({ r, onDismiss, onDelete, onEdit }) {
  const isOverdue = !r.dismissed && parseUTC(r.remindAt) < new Date();
  const d = parseUTC(r.remindAt);
  const dateStr = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  const timeStr = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  return (
    <div style={{
      background: r.dismissed ? '#F9FAFB' : '#fff',
      border: `1.5px solid ${isOverdue ? '#FCA5A5' : r.dismissed ? '#F3F4F6' : '#E5E7EB'}`,
      borderRadius: '12px', padding: '14px 16px',
      opacity: r.dismissed ? 0.6 : 1,
      transition: 'all 0.15s',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
            <p style={{ fontWeight: 700, fontSize: '15px', color: r.dismissed ? '#9CA3AF' : '#111827' }}>
              {r.dismissed && <span style={{ marginRight: '4px' }}>✓</span>}{r.title}
            </p>
            {isOverdue && <span style={{ fontSize: '11px', fontWeight: 700, color: '#DC2626', background: '#FEF2F2', padding: '2px 8px', borderRadius: '999px' }}>Overdue</span>}
            {r.repeat !== 'NONE' && (
              <span style={{ fontSize: '11px', color: '#6366F1', background: '#EEF2FF', padding: '2px 8px', borderRadius: '999px', fontWeight: 600 }}>
                🔁 {r.repeat === 'DAILY' ? 'Daily' : 'Weekly'}
              </span>
            )}
          </div>
          {r.note && <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '6px', lineHeight: 1.55 }}>{r.note}</p>}
          <p style={{ fontSize: '12px', color: isOverdue ? '#DC2626' : '#9CA3AF', fontWeight: isOverdue ? 700 : 400 }}>
            ⏰ {dateStr} at {timeStr}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
          {!r.dismissed && (
            <>
              <button onClick={() => onEdit(r)} style={S.iconBtn} title="Edit">✏️</button>
              <button onClick={() => onDismiss(r.id)} style={S.iconBtn} title="Dismiss">✓</button>
            </>
          )}
          <button onClick={() => onDelete(r.id)} style={{ ...S.iconBtn, color: '#DC2626' }} title="Delete">🗑</button>
        </div>
      </div>
    </div>
  );
}

function ReminderForm({ initial, onSave, onCancel, saving }) {
  const [title,    setTitle]    = useState(initial?.title    || '');
  const [note,     setNote]     = useState(initial?.note     || '');
  const [remindAt, setRemindAt] = useState(
    initial?.remindAt ? utcDatetimeToLocalInput(initial.remindAt) : ''
  );
  const [repeat,   setRepeat]   = useState(initial?.repeat   || 'NONE');
  const [err,      setErr]      = useState('');

  const handle = () => {
    if (!title.trim()) { setErr('Title is required'); return; }
    if (!remindAt)     { setErr('Date and time are required'); return; }
    setErr('');
    onSave({ title: title.trim(), note: note.trim() || null, remindAt: localInputToUtcDatetime(remindAt), repeat });
  };

  return (
    <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '14px', padding: '18px', marginBottom: '16px' }}>
      <p style={{ fontWeight: 700, fontSize: '15px', color: '#1D4ED8', marginBottom: '14px' }}>
        {initial ? 'Edit Reminder' : 'New Reminder'}
      </p>
      {err && <p style={{ color: '#DC2626', fontSize: '13px', marginBottom: '10px' }}>{err}</p>}
      <div style={{ display: 'grid', gap: '10px', marginBottom: '12px' }}>
        <Input label="Title *" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Follow up with vendor" />
        <Input label="Note (optional)" value={note} onChange={e => setNote(e.target.value)} placeholder="Any details…" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <Input label="Date & Time *" type="datetime-local" value={remindAt} onChange={e => setRemindAt(e.target.value)} />
          <div>
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '6px' }}>Repeat</label>
            <select value={repeat} onChange={e => setRepeat(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', background: '#fff' }}>
              {REPEAT_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <Btn size="sm" onClick={handle} loading={saving}>{initial ? 'Save Changes' : 'Add Reminder'}</Btn>
        <Btn size="sm" variant="ghost" onClick={onCancel}>Cancel</Btn>
      </div>
    </div>
  );
}

export default function Reminders() {
  const [reminders, setReminders] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');
  const [showForm,  setShowForm]  = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [showAll,   setShowAll]   = useState(false);
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await (showAll ? reminderApi.getAll() : reminderApi.getActive());
      setReminders(res.data?.data || []);
    } catch { setError('Failed to load reminders'); }
    finally { setLoading(false); }
  }, [showAll]);

  useEffect(() => { load(); }, [load]);

  // Re-render when remindAt passes (overdue section) without waiting for API refresh
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 15000);
    return () => clearInterval(id);
  }, []);

  // Refresh list periodically
  useEffect(() => {
    const id = setInterval(() => load(), 30000);
    return () => clearInterval(id);
  }, [load]);

  const handleSave = async (data) => {
    setSaving(true); setError('');
    try {
      if (editing) {
        await reminderApi.update(editing.id, data);
      } else {
        await reminderApi.create(data);
      }
      setShowForm(false); setEditing(null); load();
    } catch (e) { setError(e.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDismiss = async (id) => {
    try { await reminderApi.dismiss(id); load(); }
    catch (e) { setError(e.response?.data?.message || 'Failed'); }
  };

  const handleDelete = async (id) => {
    try { await reminderApi.delete(id); load(); }
    catch (e) { setError(e.response?.data?.message || 'Failed'); }
  };

  const handleEdit = (r) => { setEditing(r); setShowForm(true); };

  const overdue  = reminders.filter(r => !r.dismissed && parseUTC(r.remindAt) < new Date());
  const upcoming = reminders.filter(r => !r.dismissed && parseUTC(r.remindAt) >= new Date());
  const done     = reminders.filter(r => r.dismissed);

  return (
    <div style={{ padding: '20px 16px', maxWidth: '640px', margin: '0 auto' }}>
      <PageHeader title="My Reminders"
        subtitle="Private to you. Due reminders also pop up as alerts anywhere in the app."
        action={
          <div style={{ display: 'flex', gap: '8px' }}>
            <Btn size="sm" variant="ghost" onClick={() => setShowAll(s => !s)}>
              {showAll ? 'Active only' : 'Show all'}
            </Btn>
            <Btn size="sm" onClick={() => { setEditing(null); setShowForm(s => !s); }}>+ Reminder</Btn>
          </div>
        }
      />

      {error && <div style={S.err}>{error}</div>}

      {(showForm || editing) && (
        <ReminderForm initial={editing} onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditing(null); }} saving={saving} />
      )}

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {[1,2,3].map(i => <div key={i} className="skeleton" style={{ height: '80px', borderRadius: '12px' }} />)}
        </div>
      ) : reminders.length === 0 ? (
        <EmptyState icon="⏰" title="No reminders yet"
          subtitle="Set reminders for anything — follow-ups, meetings, personal tasks." />
      ) : (
        <>
          {overdue.length > 0 && (
            <Section title={`Overdue (${overdue.length})`} color="#DC2626">
              {overdue.map(r => <ReminderCard key={r.id} r={r} onDismiss={handleDismiss} onDelete={handleDelete} onEdit={handleEdit} />)}
            </Section>
          )}
          {upcoming.length > 0 && (
            <Section title={`Upcoming (${upcoming.length})`} color="#2563EB">
              {upcoming.map(r => <ReminderCard key={r.id} r={r} onDismiss={handleDismiss} onDelete={handleDelete} onEdit={handleEdit} />)}
            </Section>
          )}
          {showAll && done.length > 0 && (
            <Section title={`Dismissed (${done.length})`} color="#9CA3AF">
              {done.map(r => <ReminderCard key={r.id} r={r} onDismiss={handleDismiss} onDelete={handleDelete} onEdit={handleEdit} />)}
            </Section>
          )}
        </>
      )}
    </div>
  );
}

function Section({ title, color, children }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <p style={{ fontSize: '12px', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '8px' }}>{title}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>{children}</div>
    </div>
  );
}

const S = {
  err:     { padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', color: '#DC2626', fontSize: '14px', marginBottom: '16px' },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '4px 6px', borderRadius: '6px', lineHeight: 1 },
};
